import { getDefaultAttributeRules, getRoomLibrary } from "../library/libraries";

/**
 * Default lobby settings for an Auction room.
 * The host can change these in the lobby; they're stored on the room as `settings`.
 */
export const defaultAuctionSettings = {
    budgetPerTeam       :   1000,
    startingBid         :   10,     // Lowest price an item can sell for
    bidStep             :   10,     // Prices go up in steps of this much from the starting bid
    minItemsPerTeam     :   5,      // Fewest items a team must buy
    maxItemsPerTeam     :   6,      // Most items a team can buy
    itemsToSubmit       :   5,      // Items each team submits; only these count (at most the min to buy)
    minTeams            :   2,      // Teams with players needed before the host can start
    maxTeams            :   5,      // Teams in the room
    breakAfter          :   0,      // Pause after every X items sold (0 = no breaks)
    bringBackUnsold     :   true,   // Items the host skips come up once more, after the last item
    showBudgets         :   true,   // Everyone can see every team's budget
    showSales           :   true,   // Everyone can see who bought each item, and for how much
    showInventories     :   true,   // Everyone can see every team's items
    // Per category attribute of the library (e.g. Actor):
    // { minDifferent, maxPerValue (null = no limit), bonusOneValue, bonusEveryValue }
    attributeRules      :   {},
};

/**
 * The room's settings, with defaults filled in.
 * Older rooms have no settings, or the movie-only names (minMoviesPerTeam, maxMoviesPerActor, ...),
 * no itemsToSubmit (they submitted the min items per team), no bid rules (any whole amount),
 * and minPerValue instead of minDifferent.
 * @param {Object} room     -   The room
 * @returns {Object}        -   Settings
 */
export const getAuctionSettings = (room) => {
    const saved = room?.settings || {};
    const library = getRoomLibrary(room);

    const legacy = {};
    if (saved.minMoviesPerTeam !== undefined) legacy.minItemsPerTeam = saved.minMoviesPerTeam;
    if (saved.maxMoviesPerTeam !== undefined) legacy.maxItemsPerTeam = saved.maxMoviesPerTeam;
    const legacyActorRule = saved.maxMoviesPerActor !== undefined
        ? { Actor: { minDifferent: saved.minMoviesPerActor ?? 0, maxPerValue: saved.maxMoviesPerActor } }
        : {};

    // "Min per value" was read by hosts as "at least this many different values", so it carries over as that
    const fromLegacyRule = (rule = {}) => {
        const { minPerValue, ...rest } = rule;
        return minPerValue !== undefined && rest.minDifferent === undefined ? { ...rest, minDifferent: minPerValue } : rest;
    };

    const defaultRules = getDefaultAttributeRules(library);
    const attributeRules = Object.fromEntries(
        Object.keys(defaultRules).map((name) => [
            name,
            { ...defaultRules[name], ...(legacyActorRule[name] || {}), ...fromLegacyRule(saved.attributeRules?.[name]) },
        ])
    );

    const settings = { ...defaultAuctionSettings, ...legacy, ...saved, attributeRules };
    if (saved.itemsToSubmit === undefined) settings.itemsToSubmit = settings.minItemsPerTeam;
    if (saved.startingBid === undefined) settings.startingBid = 1;
    if (saved.bidStep === undefined) settings.bidStep = 1;
    return settings;
};
